import { useState } from 'react';
import { useSuspenseMaxAdminsQuery } from '#api/admins.queries.ts';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ButtonGroup, ButtonGroupItem } from '#components/patterns/button-group/ButtonGroup.tsx';
import { TableLayout } from '#components/patterns/layouts/table/TableLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { useUrlParam } from '#hooks/useUrlParam.ts';
import { FEATURE_UNLIMITED_FLAG } from '#util/flags.ts';
import { AddAdminModal } from '../add/AddAdminModal.tsx';
import { deletePendingAddAdmin, getPendingAddAdminEmail } from '../add/pendingAddAdmin.ts';
import { TooManyAdminsModal } from '../add/TooManyAdminsModal.tsx';
import { EmptyAdmins } from '../empty/EmptyAdmins.tsx';
import { AdminsList } from '../list/AdminsList.tsx';

export function AdminsPage() {
  const [addAdminParam, setAddAdminParam] = useUrlParam('addAdmin');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const {
    data: { data: admins },
    isFetching,
  } = useSuspenseMaxAdminsQuery();
  const { data: { ADMIN_USER_MANAGEMENT_LIMIT = 0 } = {} } = useSuspenseFeatureFlagsQuery();
  const isAdminLimitReached =
    ADMIN_USER_MANAGEMENT_LIMIT !== FEATURE_UNLIMITED_FLAG && admins.length >= ADMIN_USER_MANAGEMENT_LIMIT;
  const { user } = useAuth();
  const currentUserEmail = user?.email ?? '';
  const initialAddAdminEmail = getPendingAddAdminEmail(addAdminParam);

  function closeAddAdminModal() {
    deletePendingAddAdmin(addAdminParam);
    setAddAdminParam();
    setIsAddModalOpen(false);
  }

  let addAdminModal = null;
  if (isAddModalOpen || addAdminParam) {
    addAdminModal =
      isAdminLimitReached ?
        <TooManyAdminsModal onClose={closeAddAdminModal} adminLimit={ADMIN_USER_MANAGEMENT_LIMIT} />
      : <AddAdminModal
          initialEmail={initialAddAdminEmail}
          onCancel={closeAddAdminModal}
          onSuccess={closeAddAdminModal}
        />;
  }

  if (!admins.length) {
    return (
      <>
        <EmptyAdmins
          onAdd={() => {
            setIsAddModalOpen(true);
          }}
        />
        {addAdminModal}
      </>
    );
  }

  return (
    <>
      <TableLayout
        headerClass="padb-0"
        header={
          <PageHeader className="mb-32" title="Admins">
            <ButtonGroup>
              <ButtonGroupItem>
                <Button
                  size="small"
                  onClick={() => {
                    setIsAddModalOpen(true);
                  }}
                >
                  Add
                </Button>
              </ButtonGroupItem>
            </ButtonGroup>
          </PageHeader>
        }
      >
        <AdminsList className="mb-48" data={admins} isFetching={isFetching} currentUserEmail={currentUserEmail} />
      </TableLayout>
      {addAdminModal}
    </>
  );
}
